/**
 * The three write doors of the Postgres register — create, amend, delete — as
 * one optimistic protocol over the transaction registry (plan §3.4).
 *
 * Framework-free: takes the ledger client and a `broadcast(method, ...args)`
 * function — the registry's bus (transaction_registry.js), or `broadcastTo(hooks)`
 * over an array of handles in tests — so the JE form, the register's delete
 * confirm, the Journal Entries screen and the reconciliation tool all run the
 * same sequence.
 *
 * THE SEQUENCE, once
 *
 *   1. Broadcast the §F-shaped optimistic row (`optimisticEntryRow`) to every
 *      registered hook; the store admits it under its own predicate, pins it
 *      across the fence, and renders it in the pending style.
 *   2. Call the RPC. It commits or it refuses — `rpc()` never rejects, so the
 *      verdict is the whole answer, and a committed transaction IS the
 *      acceptance (there is no "received but not yet written" state to wait
 *      for; the org signal that follows is a no-op for this tab).
 *   3. Confirm: `confirmOptimistic(tempId, entryId, {versionId})` on every
 *      hook — the store swaps the temp row for the server's in ONE by-id
 *      fetch, and the version tells its own-event rule to ignore the event
 *      for this write even if the drain lands before that fetch does.
 *   4. On refusal: undo the optimistic step (drop the temp row / refresh the
 *      edited row) and throw a NAMED error whose message is the verdict's
 *      user-readable sentence, so `journalEntryWriteMessage` can show it — or,
 *      when the caller passed `onRefused`, hand it the error instead of throwing.
 *
 * STALE VERSION is the case the whole design exists for: `amend_entry` with an
 * `expectedVersionId` that is no longer current refuses with T3_STALE_VERSION.
 * This module refreshes the row through the registry (the "reload" action)
 * and throws `LEDGER_STALE_VERSION`, whose message already tells the user to
 * make the change again on the reloaded row.
 *
 * Clearing is only ever a reconciliation transition, so no door accepts a
 * `cleared` line flag: the form no longer builds one (record_journal_entry's
 * `buildJournalEntryData`). Whether a FORM may mint a payee is the form's
 * rule, not this module's (record_journal_entry.jsx).
 */
import { optimisticEntryRow, ymd } from "./rowShapes.js";
import { namedRpcError } from "./rpc.js";

export const LEDGER_WRITE_ERROR = "LedgerWriteError";
export const LEDGER_STALE_VERSION = "LedgerStaleVersionError";

const STALE_CODE = "T3_STALE_VERSION";

/** A verdict (or nothing) → a named Error whose message is safe to show. */
export const ledgerWriteError = (verdict, fallback) =>
  namedRpcError(verdict, {
    name: verdict?.code === STALE_CODE ? LEDGER_STALE_VERSION : LEDGER_WRITE_ERROR,
    fallback: fallback || "Could not save the transaction. Please try again.",
  });

/** True for every error this module throws — `journalEntryWriteMessage` passes their text through. */
export const isLedgerWriteError = (error) =>
  error?.name === LEDGER_WRITE_ERROR || error?.name === LEDGER_STALE_VERSION;

// --- registry fan-out ------------------------------------------------------

/**
 * `broadcast(method, ...args)` over an array (or Set) of registry handles:
 * calls `handle[method](...args)` on every handle that has it. The registry
 * context's `broadcast` is exactly this over its own set; tests use it over a
 * literal array.
 */
export const broadcastTo = (hooks) => (method, ...args) => {
  let n = 0;
  for (const hook of hooks ?? []) {
    if (hook && typeof hook[method] === "function") { hook[method](...args); n += 1; }
  }
  return n;
};

// --- rows ------------------------------------------------------------------

/**
 * The §F row the user sees before the write, and — with `versionId` /
 * `entryNumber` spread in — the row it becomes after it. `base.lines` must
 * already be stripped (each door strips once).
 *
 * `base.numbered === false` is the register form's post (0270): the entry will
 * never have an `entry_number`, and the row says so with an explicit
 * `journalEntry: null`. A row that WILL be numbered leaves the field ABSENT,
 * which is how the optimistic row has always represented "the number is the
 * server's to allocate and has not arrived yet" — the two are different states
 * and `ledgerPredicate.matches` reads them apart.
 */
export function buildRow(base, serverFields = {}) {
  const { id, date, createdAt, memo, description, checkNumber, lines, kind, numbered } = base;
  const row = optimisticEntryRow({
    id,
    date,
    createdAt,
    memo: memo ?? null,
    description: description ?? null,
    checkNumber: checkNumber || null,
    lines,
    kind: kind ?? "manual",
  });
  if (numbered === false) row.journalEntry = null;
  const { versionId, entryNumber } = serverFields;
  if (versionId != null) row.versionId = versionId;
  if (entryNumber != null) {
    row.entryNumber = entryNumber;
    row.journalEntry = entryNumber;
  }
  return row;
}

const entryDateArg = (date) => ymd(date, { local: true });

// --- the three doors -------------------------------------------------------

/**
 * Create. Returns the server's entry id.
 * @param {object} p
 * @param {{postEntry: Function}} p.ledger
 * @param {(method: string, ...args: any[]) => number} p.broadcast  the registry bus
 * @param {boolean} [p.numbered=true]  false = post without an entry_number
 *   (0270; the bank-register form). A CHOICE only here: an amend never
 *   renumbers, so `amendJournalEntry` reads the same fact off the row it was
 *   handed instead of taking an option.
 */
export async function postJournalEntry({
  ledger, broadcast, orgId, date, memo, description, checkNumber, lines, kind = "manual",
  sourceRef = null, tempId, createdAt = new Date(), requestId, numbered = true,
}) {
  const base = { id: tempId, date, createdAt, memo, description, checkNumber, lines, kind, numbered };
  broadcast("addOptimisticTransaction", buildRow(base));

  const { data, error } = await ledger.postEntry({
    orgId,
    entryDate: entryDateArg(date),
    lines: base.lines,
    kind,
    memo: memo ?? null,
    description: description ?? null,
    checkNumber: checkNumber || null,
    sourceRef,
    requestId,
    numbered,
  });
  if (error) {
    broadcast("removeTransaction", tempId);
    throw ledgerWriteError(error);
  }
  broadcast("confirmOptimistic", tempId, data.entry_id, { versionId: data.version_id });
  return data.entry_id;
}

/**
 * Amend. `entryToEdit` is the §F row the user opened (it carries `versionId`,
 * `createdAt`, `kind`, `entryNumber`). Returns the entry id.
 */
export async function amendJournalEntry({
  ledger, broadcast, orgId, entryToEdit, date, memo, description, checkNumber, lines, changeReason, requestId,
}) {
  const entryId = entryToEdit?.id;
  const expectedVersionId = entryToEdit?.versionId;
  if (!entryId || !expectedVersionId) {
    // A row without a version has not been read from Postgres — the edit door
    // cannot be safe. Never fall back to last-write-wins.
    throw ledgerWriteError(null, "This transaction needs to reload before it can be edited. Close and reopen it.");
  }

  // An amend never renumbers, so the numbering fact is READ off the row rather
  // than chosen: a row that has no entry number never gets one, and the
  // optimistic row has to say so explicitly (`journalEntry: null`). Left
  // absent, it would read as "numbered, not allocated yet" — and amending a
  // bank-register entry from the Journal Entries screen would broadcast a row
  // the numbered-only store ADMITS, render it, and then drop it on the confirm
  // when the server's real row comes back unnumbered.
  const entryNumber = entryToEdit.entryNumber ?? entryToEdit.journalEntry ?? null;
  const base = {
    id: entryId,
    date,
    createdAt: entryToEdit.createdAt,
    memo,
    description: description ?? entryToEdit.description ?? null,
    checkNumber,
    lines,
    kind: entryToEdit.kind,
    numbered: entryNumber != null,
  };
  broadcast("addOptimisticTransaction", buildRow(base, { versionId: expectedVersionId, entryNumber }));

  const { data, error } = await ledger.amendEntry({
    orgId,
    entryId,
    expectedVersionId,
    entryDate: entryDateArg(date),
    lines: base.lines,
    memo: memo ?? null,
    description: base.description,
    checkNumber: checkNumber || null,
    changeReason: changeReason ?? null,
    requestId,
  });
  if (error) {
    // The reload action: the row the user sees goes back to the server's truth
    // (for a stale version, that is the OTHER user's change).
    broadcast("refresh", [entryId]);
    throw ledgerWriteError(error);
  }
  broadcast("confirmOptimistic", entryId, entryId, { versionId: data.version_id });
  return entryId;
}

/**
 * Delete. ONE call: `reconciliation_marks` cascade with the entry (plan §3.4),
 * so there is no reconciliation two-step. The optimistic remove is broadcast
 * first unless the caller already did it (`alreadyRemoved`). A refusal
 * refreshes the row back; with `onRefused` the error goes there (and the
 * promise resolves null), otherwise it is thrown.
 */
export async function deleteJournalEntry({
  ledger, broadcast, orgId, entryId, expectedVersionId = null, alreadyRemoved = false, onRefused = null,
}) {
  if (!alreadyRemoved) broadcast("removeTransaction", entryId);
  const { error } = await ledger.deleteEntry({ orgId, entryId, expectedVersionId: expectedVersionId ?? null });
  if (error) {
    broadcast("refresh", [entryId]); // restore the row
    const err = ledgerWriteError(error, "Could not delete the transaction. Please try again.");
    if (onRefused) { onRefused(err); return null; }
    throw err;
  }
  return entryId;
}
